import type { FavoriteTargetType, FavoritesSearchParams } from "@compass/shared/schema/favorite";
import { PRIVATE_FAVORITE_TYPES, PUBLIC_FAVORITE_TYPES } from "@compass/shared/schema/favorite";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { Star } from "lucide-react";
import { SocialPostWrapper } from "screens/social/post/SocialPostWrapper";
import { Spinner } from "components/elements";
import { ChartCard } from "components/elements/ChartCard";
import { CompassEventCard } from "components/elements/CompassEventCard";
import { DigestCard } from "components/elements/DigestCard";
import { DiscussionCard } from "components/elements/DiscussionCard";
import { EventCard } from "components/elements/EventCard";
import { VendorCard } from "components/elements/VendorCard";
import { trpc } from "lib/trpc";
import { BlogPostPreview } from "screens/blog/BlogPostPreview";
import { RssFeedCard } from "screens/rss/RssFeedCard";
import type { ClientPost } from "@compass/shared/types";

const PRIVATE_FILTERS: { label: string; value: FavoriteTargetType }[] = [
    { label: "Discussions", value: "discussion" },
    { label: "Charts", value: "chart" },
    { label: "Private Events", value: "compassEvent" },
];

const PUBLIC_FILTERS: { label: string; value: FavoriteTargetType }[] = [
    { label: "Posts", value: "post" },
    { label: "Articles", value: "feedArticle" },
    { label: "Blog Posts", value: "blogPost" },
    { label: "Events", value: "event" },
    { label: "Vendors", value: "vendor" },
    { label: "AI Digests", value: "sitewideSummary" },
];

const PAGE_SIZE = 20;

export const Favorites = () => {
    const router = useRouter();
    const search = (router.state.currentLocation.search ?? {}) as FavoritesSearchParams;
    const navigate = useNavigate();

    const selectedTypes = new Set<FavoriteTargetType>(
        search.types ? (search.types.split(",") as FavoriteTargetType[]) : [],
    );
    const page = search.page ?? 1;

    const hasAnyFilter = selectedTypes.size > 0;

    const isPrivateAll = PRIVATE_FAVORITE_TYPES.every((t) => selectedTypes.has(t));
    const isPublicAll = PUBLIC_FAVORITE_TYPES.every((t) => selectedTypes.has(t));

    const { data, isLoading, isError } = trpc.favorites.getUserFavorites.useQuery({
        filterTypes: selectedTypes.size > 0 ? Array.from(selectedTypes) : undefined,
        page,
        pageSize: PAGE_SIZE,
    });

    const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

    const setTypes = (next: Set<FavoriteTargetType>) => {
        navigate({
            search: {
                ...search,
                page: undefined,
                types: next.size > 0 ? Array.from(next).join(",") : undefined,
            },
            replace: true,
        });
    };

    const toggleType = (value: FavoriteTargetType) => {
        const next = new Set(selectedTypes);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        setTypes(next);
    };

    const toggleSuperset = (group: FavoriteTargetType[]) => {
        const allSelected = group.every((t) => selectedTypes.has(t));
        const next = new Set(selectedTypes);
        if (allSelected) {
            group.forEach((t) => next.delete(t));
        } else {
            group.forEach((t) => next.add(t));
        }
        setTypes(next);
    };

    const clearFilters = () => navigate({ search: {}, replace: true });

    return (
        <div className="mx-auto w-full max-w-7xl px-6 pt-6 pb-12">
            <div className="mb-6 flex items-center gap-3">
                <Star className="h-7 w-7 text-yellow-500" fill="currentColor" />
                <h1 className="font-sans text-[28px] font-semibold text-[#424242]">Favorites</h1>
            </div>

            <div className="mb-2 flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap gap-1">
                    <button
                        onClick={() => toggleSuperset(PRIVATE_FAVORITE_TYPES)}
                        className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                            isPrivateAll
                                ? "bg-[#424242] text-white"
                                : "border border-[#424242] text-[#424242] hover:bg-gray-100"
                        }`}
                    >
                        Private data
                    </button>
                    {PRIVATE_FILTERS.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => toggleType(opt.value)}
                            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                                selectedTypes.has(opt.value)
                                    ? "bg-[#424242] text-white"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <div className="h-5 w-px bg-gray-200" />
                <div className="flex flex-wrap gap-1">
                    <button
                        onClick={() => toggleSuperset(PUBLIC_FAVORITE_TYPES)}
                        className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                            isPublicAll
                                ? "bg-[#2DA7E0] text-white"
                                : "border border-[#2DA7E0] text-[#2DA7E0] hover:bg-blue-50"
                        }`}
                    >
                        Public data
                    </button>
                    {PUBLIC_FILTERS.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => toggleType(opt.value)}
                            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                                selectedTypes.has(opt.value)
                                    ? "bg-[#2DA7E0] text-white"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>
            {hasAnyFilter && (
                <div className="mb-4 text-right">
                    <button
                        onClick={clearFilters}
                        className="text-xs text-gray-400 hover:text-gray-600"
                    >
                        Clear filters
                    </button>
                </div>
            )}
            {!hasAnyFilter && <div className="mb-4" />}

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <Spinner className="h-8 w-8 text-gray-400" />
                </div>
            ) : isError ? (
                <div className="py-16 text-center text-red-500">Failed to load favorites.</div>
            ) : !data?.data || data.data.length === 0 ? (
                <div className="py-16 text-center">
                    <Star className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                    <p className="text-lg text-gray-500">No favorites yet</p>
                    <p className="mt-1 text-sm text-gray-400">
                        Star content across the app to see it here.
                    </p>
                </div>
            ) : (
                <>
                    <div className="columns-1 gap-4 space-y-4 sm:columns-2 lg:columns-3 xl:columns-4">
                        {data.data.map((item) => (
                            <div key={item.favoriteId} className="break-inside-avoid">
                                {item.type === "post" && (
                                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                                        <SocialPostWrapper
                                            post={item.data as ClientPost}
                                            compact
                                            target="_blank"
                                        />
                                    </div>
                                )}
                                {item.type === "feedArticle" && (
                                    <RssFeedCard
                                        title={item.data.title}
                                        description={item.data.excerpt}
                                        date={item.data.publishedAt}
                                        link={item.data.url}
                                        imageUrl={item.data.imageUrl}
                                        feedSourceTitle={item.data.feedSourceTitle}
                                        articleId={item.data.id}
                                    />
                                )}
                                {item.type === "blogPost" && (
                                    <Link
                                        to="/p/$slug"
                                        params={{ slug: item.data.slug }}
                                        target="_blank"
                                        className="block no-underline"
                                    >
                                        <BlogPostPreview post={item.data} />
                                    </Link>
                                )}
                                {item.type === "discussion" && (
                                    <DiscussionCard discussion={item.data} target="_blank" />
                                )}
                                {item.type === "chart" && (
                                    <ChartCard chart={item.data} target="_blank" />
                                )}
                                {item.type === "event" && (
                                    <EventCard event={item.data} target="_blank" />
                                )}
                                {item.type === "compassEvent" && (
                                    <CompassEventCard compassEvent={item.data} target="_blank" />
                                )}
                                {item.type === "vendor" && (
                                    <VendorCard vendor={item.data} target="_blank" />
                                )}
                                {item.type === "sitewideSummary" && (
                                    <DigestCard summary={item.data} target="_blank" />
                                )}
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="mt-8 flex items-center justify-center gap-2">
                            <button
                                onClick={() => {
                                    const prev = Math.max(1, page - 1);
                                    navigate({
                                        search: { ...search, page: prev === 1 ? undefined : prev },
                                        replace: true,
                                    });
                                }}
                                disabled={page === 1}
                                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() =>
                                    navigate({
                                        search: { ...search, page: Math.min(totalPages, page + 1) },
                                        replace: true,
                                    })
                                }
                                disabled={page === totalPages}
                                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
